import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import MailIcon from "@mui/icons-material/Mail";
import VisibilityIcon from "@mui/icons-material/Visibility";


import {
  Box,
  Stack,
  Typography,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  IconButton,
  Drawer,
  Card,
  CardContent,
  useMediaQuery,
  InputLabel,
  FormControl,
} from "@mui/material";


import { motion } from "framer-motion";

import { useQueryClient } from "@tanstack/react-query";
import { useOnlineStatus } from "@/offline/useOnlineStatus";
import { db } from "@/db";
import { queueNote } from "@/db_utils";
import { useAuth } from "@/auth/AuthContext";
import {
  formatNoteForDisplay,
  formatTimestamp,
  mergeNotes,
  statusDisplay,
} from "@/offline/noteUtils";
import { makeClientRequestId, useLocationStore } from "@/location/locationStore";
import { STATUS_OPTIONS } from "@/search/constants";

export default function AddressDetail() {

  const queryClient = useQueryClient();
  const online = useOnlineStatus();
  const { user } = useAuth();
  const requestLocation = useLocationStore((s) => s.requestLocation);
  const lastKnownLocation = useLocationStore((s) => s.lastLocation);
  const pendingOfflineLocationByLocalId = useRef<Record<string, number>>({});
  const wasOnline = useRef<boolean>(online);

  const locationFields = (loc: { latitude: number; longitude: number; accuracy: number | null; capturedAt: string; method: string }) => ({
    latitude: loc.latitude,
    longitude: loc.longitude,
    accuracy: loc.accuracy,
    location_at: loc.capturedAt,
    location_method: loc.method,
  });

  const { udprn } = useParams();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const [saving, setSaving] = useState(false);

  const statusRef = useRef<HTMLInputElement | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [status, setStatus] = useState("");
  const [followupDate, setFollowupDate] = useState<string | null>(null);

  // Customer fields
  // const [custTitle, setCustTitle] = useState("");
  // const [custFirst, setCustFirst] = useState("");
  // const [custMiddle, setCustMiddle] = useState("");
  // const [custLast, setCustLast] = useState("");
  const [custName, setCustName] = useState("");

  const [custEmail, setCustEmail] = useState("");
  const [custPhone, setCustPhone] = useState("");

  // Regular / Quoted fields
  const [frequency, setFrequency] = useState("");
  const [price, setPrice] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [notificationType, setNotificationType] = useState("");

  
  const isRegularOrQuoted =
  status === "REGULAR" || status === "QUOTED";


  const numericUdprn = useMemo(() => Number(udprn), [udprn]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["address", udprn],
    enabled: !!udprn,
    retry: false,
    placeholderData: () =>
      queryClient.getQueryData(["address", udprn]) as any,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: "always",
    staleTime: 0,
    queryFn: async () => {
      if (!udprn || Number.isNaN(numericUdprn)) {
        throw new Error("Invalid address id");
      }

      if (!online) {
        const address = await db.addresses.where("udprn").equals(numericUdprn).first();
        if (!address) {
          throw new Error("Address not available offline");
        }

        const [customer, notes, queuedNotes] = await Promise.all([
          db.customers.where("udprn").equals(numericUdprn).first(),
          db.notesSynced.where("udprn").equals(numericUdprn).toArray(),
          db.notesQueue.where("udprn").equals(numericUdprn).toArray(),
        ]);

        const { latest_note, notes: mergedNotes } = mergeNotes(
          notes,
          queuedNotes,
          user?.username
        );

        return {
          ...address,
          customer: customer ?? null,
          latest_note,
          notes: mergedNotes,
          offline: true,
        };
      }

      const res = await api.get(`address/${udprn}/`);
      return res.data;
    },
  });

  useEffect(() => {
    if (!wasOnline.current && online) {
      refetch();
    }
    wasOnline.current = online;
  }, [online, refetch]);

  useEffect(() => {
    if (data?.customer) {
      // setCustTitle(data.customer.title ?? "");
      // setCustFirst(data.customer.first_name ?? "");
      // setCustMiddle(data.customer.middle_names ?? "");
      // setCustLast(data.customer.last_name ?? "");
      setCustName(data.customer.name ?? "");
    }
  }, [data]);

  const latestNote = data?.latest_note;
  const hasPendingNote = !!data?.notes?.some((n: any) => n.pending);

  const patchSearchQueries = (targetUdprn: number, latestNotePayload: any, flashTag: string) => {
    const queries = queryClient.getQueriesData<any>({ queryKey: ["addresses"] });

    queries.forEach(([key, value]) => {
      if (!value?.pages) return;

      const updatedPages = value.pages.map((page: any) => {
        if (!page?.results) return page;

        const updatedResults = page.results.map((addr: any) =>
          addr.udprn === targetUdprn
            ? {
                ...addr,
                latest_note: latestNotePayload,
                flashTag,
              }
            : addr
        );

        return { ...page, results: updatedResults };
      });

      queryClient.setQueryData(key, { ...value, pages: updatedPages });
    });
  };

  // Drawer config depending on platform
  const paperSx = isDesktop
    ? { width: 480 }
    : {
        height: "85vh",
        borderRadius: "16px 16px 0 0",
        overflow: "hidden",
      };

  // const close = () => navigate(-1);
  const close = () => {
  navigate({
    pathname: "/search",
    search: window.location.search,   // keep the existing filters
  });
};

  const submitNote = async () => {
    if (!udprn) return;
    if (!status) {
      setStatusError("Select a status to save the note.");
      statusRef.current?.focus();
      statusRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setStatusError(null);
    const endpoint = `address/${udprn}/notes/`;
    const client_created_at = new Date().toISOString();
    const local_id = makeClientRequestId();
    const initialLocation = lastKnownLocation;
    const body = {
      status,
      note: noteText,
      followup_date: followupDate || null,
      frequency: isRegularOrQuoted ? frequency || null : null,
      price: isRegularOrQuoted ? price || null : null,
      payment_type: isRegularOrQuoted ? paymentType || null : null,
      notification_type: isRegularOrQuoted ? notificationType || null : null,
      client_created_at,
      customer: isRegularOrQuoted
        ? {
            name: custName || null,
            email: custEmail || null,
            phone: custPhone || null,
          }
        : null,
      username: user?.username ?? "You (offline)",
      local_id,
    };
    const bodyWithLocation = initialLocation
      ? { ...body, ...locationFields(initialLocation) }
      : body;
    const locationPromise = requestLocation("note");

    if (online) {
      try {
        await api.post(endpoint, bodyWithLocation);
      } catch (err: any) {
        const statusCode = err?.response?.status;
        if (initialLocation && statusCode && statusCode >= 400 && statusCode < 500) {
          await api.post(endpoint, body);
        } else {
          throw err;
        }
      }
    } else {
      const queuedId = await queueNote(numericUdprn, endpoint, bodyWithLocation);
      pendingOfflineLocationByLocalId.current[local_id] = queuedId;
    }

    const pendingNote = formatNoteForDisplay(
      {
        ...bodyWithLocation,
        pending: !online,
        created_at: online ? new Date().toISOString() : client_created_at,
      },
      user?.username
    );
    patchSearchQueries(numericUdprn, pendingNote, client_created_at);

    setNoteText("");
    await refetch();
    queryClient.invalidateQueries({ queryKey: ["addresses"], exact: false });
    close();

    locationPromise
      .then(async (res) => {
        if (res.state !== "granted") {
          if (!online) {
            delete pendingOfflineLocationByLocalId.current[local_id];
          }
          return;
        }
        if (online) {
          const locFields = locationFields(res.location);
          await api.patch(`notes/${local_id}/location/`, locFields).catch(() => {});
          return;
        }

        const queuedId = pendingOfflineLocationByLocalId.current[local_id];
        if (queuedId === undefined) return;

        const queued = await db.notesQueue.get(queuedId);
        if (!queued) return;
        const existingPayload = queued.payload ?? {};
        const existingBody = existingPayload.body ?? existingPayload ?? {};
        const nextPayload = {
          ...existingPayload,
          body: {
            ...existingBody,
            ...locationFields(res.location),
          },
        };
        await db.notesQueue.update(queuedId, { payload: nextPayload });
        delete pendingOfflineLocationByLocalId.current[local_id];
      })
      .catch(() => {});
  };

  const quickSubmit = async (quickStatus: string) => {
    if (saving || !udprn) return;
    setSaving(true);

    const endpoint = `address/${udprn}/notes/`;
    const client_created_at = new Date().toISOString();
    const local_id = makeClientRequestId();
    const initialLocation = lastKnownLocation;
    const body = {
      status: quickStatus,
      note: "",
      client_created_at,
      username: user?.username ?? "You (offline)",
      local_id,
    };
    const bodyWithLocation = initialLocation
      ? { ...body, ...locationFields(initialLocation) }
      : body;
    const locationPromise = requestLocation("note");

    try {
      if (online) {
        try {
          await api.post(endpoint, bodyWithLocation);
        } catch (err: any) {
          const statusCode = err?.response?.status;
          if (initialLocation && statusCode && statusCode >= 400 && statusCode < 500) {
            await api.post(endpoint, body);
          } else {
            throw err;
          }
        }
      } else {
        const queuedId = await queueNote(numericUdprn, endpoint, bodyWithLocation);
        pendingOfflineLocationByLocalId.current[local_id] = queuedId;
      }

      const pendingNote = formatNoteForDisplay(
        {
          ...bodyWithLocation,
          pending: !online,
          created_at: online ? new Date().toISOString() : client_created_at,
        },
        user?.username
      );
      patchSearchQueries(numericUdprn, pendingNote, client_created_at);

      await refetch();
      queryClient.invalidateQueries({ queryKey: ["addresses"], exact: false });
      close();

      locationPromise
        .then(async (res) => {
          if (res.state !== "granted") {
            if (!online) {
              delete pendingOfflineLocationByLocalId.current[local_id];
            }
            return;
          }
          if (online) {
            const locFields = locationFields(res.location);
            await api.patch(`notes/${local_id}/location/`, locFields).catch(() => {});
            return;
          }

          const queuedId = pendingOfflineLocationByLocalId.current[local_id];
          if (queuedId === undefined) return;

          const queued = await db.notesQueue.get(queuedId);
          if (!queued) return;
          const existingPayload = queued.payload ?? {};
          const existingBody = existingPayload.body ?? existingPayload ?? {};
          const nextPayload = {
            ...existingPayload,
            body: {
              ...existingBody,
              ...locationFields(res.location),
            },
          };
          await db.notesQueue.update(queuedId, { payload: nextPayload });
          delete pendingOfflineLocationByLocalId.current[local_id];
        })
        .catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor={isDesktop ? "right" : "bottom"}
      open={true}
      onClose={close}
      PaperProps={{ sx: paperSx }}
      ModalProps={{
        keepMounted: true,
        disablePortal: true,
        disableEnforceFocus: true,
        disableAutoFocus: true,
        disableRestoreFocus: true,
  }}
    >
{/* MOBILE HEADER — iOS style */}
{!isDesktop && (
  <motion.div
    initial={{ height: 60 }}
    animate={{ height: 60 }}
    transition={{ duration: 0.25, ease: "easeOut" }}
  >
    <Box
      sx={{
        py: 1,
        px: 2,
        background: "white",
        borderBottom: "1px solid #eee",
        display: "flex",
        alignItems: "center",
        position: "relative",
        height: "60px",
      }}
    >
      {/* Circular X button on the left */}
      <IconButton
        onClick={close}
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: "#f2f2f2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&:hover": { background: "#e5e5e5" },
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>

      {/* Centered title */}
      <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{ flexGrow: 1, textAlign: "center", ml: -4 }}
      >
        Details
      </Typography>
    </Box>
  </motion.div>
)}


      {/* DESKTOP HEADER */}
      {isDesktop && (
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #eee",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Address Details
          </Typography>
          <Box flexGrow={1} />
          <IconButton onClick={close}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}

      {/* Content */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        sx={{ p: 2, pb: 6, overflowY: "auto", height: "100%" }}
      >
        {/* Address Card */}
        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
          {isLoading && !data ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : isError ? (
            <Stack spacing={1}>
              <Typography color="error">
                {error instanceof Error
                  ? error.message
                  : "Unable to load address. Check your connection or try again later."}
              </Typography>
              <Button variant="outlined" onClick={close}>Close</Button>
            </Stack>
          ) : !data ? (
            <Typography color="text.secondary">
              No address data available.
            </Typography>
          ) : (
            <>
              <Typography variant="h6" fontWeight={700}>
                {data.to_display}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {data.customer
                  ? `${data.customer.name ?? ""}`.trim(): data.postcode}
              </Typography>
              {latestNote && (
                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Latest Note {latestNote.pending ? "(Queued)" : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{statusDisplay(latestNote.status, latestNote.status_display)}</strong>
                    {latestNote.note ? ` — ${latestNote.note}` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {latestNote.username ? `${latestNote.username} — ` : ""}
                    {formatTimestamp(latestNote.created_at ?? latestNote.client_created_at)}
                  </Typography>
                </Stack>
              )}
              {data.customer && (
  <Stack spacing={0.5} sx={{ mt: 1 }}>
    {data.customer.phone && (
      <Typography
        component="a"
        href={`tel:${data.customer.phone}`}
        color="primary"
        sx={{ textDecoration: "none", fontSize: "0.9rem" }}
      >
        📞 {data.customer.phone}
      </Typography>
    )}

    {data.customer.email && (
      <Typography
        component="a"
        href={`mailto:${data.customer.email}`}
        color="primary"
        sx={{ textDecoration: "none", fontSize: "0.9rem" }}
      >
        ✉️ {data.customer.email}
      </Typography>
    )}
  </Stack>
)}
            </>
          )}
        </Paper>

        <Box height={16} />

        {/* Add Note */}
        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Add Note
          </Typography>

<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
  {[
    {
      label: "Not interested",
      status: "NOT_INTERESTED",
      icon: <BlockIcon sx={{ fontSize: 36 }} />,
    },
    {
      label: "Left leaflet",
      status: "LEAFLET",
      icon: <MailIcon sx={{ fontSize: 36 }} />,
    },
    {
      label: "Dirty windows",
      status: "OBSERVED_DIRTY",
      icon: <VisibilityIcon sx={{ fontSize: 36 }} />,
    },
  ].map((item) => (
    <Button
      key={item.status}
      onClick={() => quickSubmit(item.status)}
      disabled={saving}
      variant="outlined"
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        borderRadius: 3,
        py: 2.5,
        height: 110,
        borderWidth: 2,
        textTransform: "none",
      }}
    >
      {item.icon}
      <Typography
        variant="caption"
        sx={{ mt: 1, fontWeight: 600, fontSize: "0.75rem" }}
      >
        {item.label.toUpperCase()}
      </Typography>
    </Button>
  ))}
</Stack>


          <Stack spacing={2}>

            <FormControl fullWidth size="small" error={!!statusError}>
  <InputLabel>Status</InputLabel>
  <Select
    label="Status"
    fullWidth
    size="small"
    value={status}
    onChange={(e) => {
      setStatus(e.target.value);
      if (statusError) setStatusError(null);
    }}
    inputRef={statusRef}
  >
    {STATUS_OPTIONS.map((opt) => (
      <MenuItem key={opt.value} value={opt.value}>
        {opt.label}
      </MenuItem>
    ))}
  </Select>
  {statusError && <FormHelperText>{statusError}</FormHelperText>}
</FormControl>

            <TextField
              multiline
              minRows={3}
              placeholder="Write a note…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              size="small"
              fullWidth
            />

            <TextField
              label="Follow-up date"
              type="date"
              value={followupDate ?? ""}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setFollowupDate(e.target.value || null)}
              onFocus={() => {
                if (!followupDate) {
                  const nextWeek = new Date();
                  nextWeek.setDate(nextWeek.getDate() + 7);
                  setFollowupDate(nextWeek.toISOString().substring(0, 10));
                }
              }}
              fullWidth
              size="small"
            />

            {/* if select quote or regular */}
            {isRegularOrQuoted && (
              <Stack spacing={2} sx={{ mt: 1, borderTop: "1px solid #eee", pt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Customer Details
                </Typography>

                {/* Customer name fields */}
                {/* <Stack direction="row" spacing={2}>
                  <TextField
                    label="Title"
                    size="small"
                    value={custTitle}
                    onChange={(e) => setCustTitle(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="First name"
                    size="small"
                    value={custFirst}
                    onChange={(e) => setCustFirst(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <TextField
                  label="Middle name(s)"
                  size="small"
                  value={custMiddle}
                  onChange={(e) => setCustMiddle(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Last name"
                  size="small"
                  value={custLast}
                  onChange={(e) => setCustLast(e.target.value)}
                  fullWidth
                /> */}

                <TextField
                  label="Name"
                  size="small"
                  value={custName}
                  onChange={(e) => setCustName(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Email"
                  size="small"
                  value={custEmail}
                  onChange={(e) => setCustEmail(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Phone"
                  size="small"
                  value={custPhone}
                  onChange={(e) => setCustPhone(e.target.value)}
                  fullWidth
                />

                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>
                  Job Details
                </Typography>

                <FormControl fullWidth size="small">
                <InputLabel size="small" id="select-addressdetail-frequency">Frequency</InputLabel>
                <Select
                  labelId="select-addressdetail-frequency"
                  size="small"
                  label="Frequency"
                  fullWidth
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                >
                  <MenuItem value="4W">4 Weekly</MenuItem>
                  <MenuItem value="8W">8 Weekly</MenuItem>
                  <MenuItem value="ADHOC">Ad Hoc</MenuItem>
                </Select>
                </FormControl>

                <TextField
                  label="Price (£)"
                  size="small"
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  fullWidth
                />

                <FormControl fullWidth size="small">
                <InputLabel size="small" id="select-addressdetail-paymenttype">Payment Type</InputLabel>
                <Select
                  labelId="select-addressdetail-paymenttype"
                  size="small"
                  fullWidth
                  label="Payment Type"
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                >
                  <MenuItem value="CASH">Cash</MenuItem>
                  <MenuItem value="TRANSFER">Transfer</MenuItem>
                </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                <InputLabel size="small" id="select-addressdetail-notificationtype">Notification Type</InputLabel>  
                <Select
                  labelId="select-addressdetail-notificationtype"
                  size="small"
                  fullWidth
                  label="Notification Type"
                  value={notificationType}
                  onChange={(e) => setNotificationType(e.target.value)}
                >
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="CALL">Call</MenuItem>
                </Select>
                </FormControl>
              </Stack>
            )}


            <Button
              variant="contained"
              fullWidth
              disabled={false}
              onClick={submitNote}
            >
              Save Note
            </Button>
          </Stack>
        </Paper>

        <Box height={16} />

        {/* Notes */}
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Notes {hasPendingNote && (
  <Chip size="small" color="warning" label="Queued notes pending sync" sx={{ ml: 1 }} />
)}
        </Typography>


        <Stack spacing={2}>
          {data?.notes?.map((note: any, idx: number) => {
            const displayStatus = statusDisplay(note.status, note.status_display);
            const displayDate = formatTimestamp(note.created_at ?? note.client_created_at);
            const key =
              (note as any).id ??
              (note as any).local_id ??
              (note as any).localId ??
              idx;
            return (
            <Card key={key} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={1}>
                  <Chip
                    label={displayStatus}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600, alignSelf: "flex-start" }}
                  />
                  {note.pending && (
                    <Chip
                      label="Queued"
                      color="warning"
                      size="small"
                      sx={{ fontWeight: 600, alignSelf: "flex-start" }}
                    />
                  )}

                  {note.note && (
                    <Typography variant="body2">{note.note}</Typography>
                  )}

                  <Stack spacing={0.5}>


                    {/* Extra fields for REGULAR or QUOTED */}
                    {(note.status === "REGULAR" ||
                      note.status === "QUOTED") && (
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        {note.frequency && (
                          <Typography variant="caption" color="text.secondary">
                            Frequency: {note.frequency_display}
                          </Typography>
                        )}
                        {note.notification_type && (
                          <Typography variant="caption" color="text.secondary">
                            Notify by: {note.notification_type_display}
                          </Typography>
                        )}
                        {note.payment_type && (
                          <Typography variant="caption" color="text.secondary">
                            Payment: {note.payment_type_display}
                          </Typography>
                        )}
                        {note.price !== null && (
                          <Typography variant="caption" color="text.secondary">
                            Price: £{note.price}
                          </Typography>
                        )}
                        
                      </Stack>
                    )}

                    {/* Follow-up date (for all statuses) */}
                    {note.followup_date && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Follow-up: {new Date(note.followup_date).toLocaleDateString()}
                      </Typography>
                    )}
                  </Stack>

                    {/* Username + Date */}
                    <Typography variant="caption" color="text.secondary">
                      {note.username ? `${note.username} — ` : ""}
                      {displayDate}
                    </Typography>

                </Stack>
              </CardContent>
            </Card>
          )})}

          {data?.notes?.length === 0 && (
            <Typography textAlign="center" color="text.secondary" sx={{ mt: 2 }}>
              No notes yet.
            </Typography>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
